import { useLocalization } from '@magicdoor/ui';
import { useLocation, useMatch } from '@solidjs/router';
import { createEffect, createMemo, createSignal, onCleanup, onMount } from 'solid-js';
import Breadcrumb from '~/components/common/Breadcrumb';
import { useMenu } from '~/components/layouts/Menu/MenuContext';
import { getTenantFullName } from '~/components/tenants';
import { useOwners, useProperties, useRentalApplication, useTenants, useUnitListings, useUnits, useVendors } from '~/contexts/global';
import usePresenter from '~/framework/hooks/usePresenter';
import { useGoBack } from '~/hooks';
import { TitlePresenter } from '~/pdfsigner/presenters/TitlePresenter';
import { emptyPlaceholder } from '~/utils/constant';
import type { BreadcrumbItem } from '~/components/common/Breadcrumb';

const BreadcrumbContainer = () => {
  const { t } = useLocalization();
  const location = useLocation();
  const goBack = useGoBack();
  const menu = useMenu();

  const { current: currentProperty } = useProperties();
  const { unitListingDetail } = useUnitListings();
  const { applicationStore } = useRentalApplication();
  const { unit } = useUnits();
  const { current: currentTenant } = useTenants();
  const { current: currentOwner } = useOwners();
  const { current: currentVendor } = useVendors();

  const { model: presentableTitle } = usePresenter(TitlePresenter);

  const [backLink, setBackLink] = createSignal<string | (() => void) | undefined>();
  const [visibleBreadcrumbs, setVisibleBreadcrumbs] = createSignal<BreadcrumbItem[]>([]);

  let containerRef: HTMLDivElement | null = null;

  function hasId(path: string) {
    return /\d{10,}/.test(path);
  }

  const reportPages: Record<string, { label: string; link: string }> = {
    '/cash-flow': { label: t('Cash flow'), link: '/reports/cash-flow' },
    '/general-ledger': { label: t('General ledger'), link: '/reports/general-ledger' },
    '/balance-sheet': { label: t('Balance sheet'), link: '/reports/balance-sheet' },
    '/income-statement': { label: t('Income statement report'), link: '/reports/income-statement' },
    '/owner-statement': { label: t('Owner statement'), link: '/reports/owner-statement' },
    '/rent-roll': { label: t('Rent roll'), link: '/reports/rent-roll' },
    '/property-directory': { label: t('Property directory'), link: '/reports/property-directory' },
    '/owner-directory': { label: t('Owner directory'), link: '/reports/owner-directory' },
    '/tenant-directory': { label: t('Tenant directory'), link: '/reports/tenant-directory' },
    '/bank-account-activity': { label: t('Bank account activity report'), link: '/reports/bank-account-activity' },
    '/lease-unpaid-bills': { label: t('Lease unpaid bills'), link: '/reports/lease-unpaid-bills' },
    '/delinquency-report': { label: t('Delinquency report'), link: '/reports/delinquency-report' },
  };

  const breadcrumbItems = createMemo(() => {
    const path = location.pathname.toLowerCase();
    if (path === '/') {
      setBackLink('home');
      return [{ label: t('Home') }];
    }

    if (path.startsWith('/onboarding')) {
      return [{ label: t('Onboarding') }];
    }

    const property = currentProperty();
    const portfolioId = property?.portfolio?.id;
    const propertyId = property?.id;
    const unitId = unit()?.id;

    const propertyName = property?.name;
    const portfolioName = property?.portfolio.name;
    const unitName = unit()?.name;
    const tProperty = t('Properties');
    const tLoading = t('Loading');
    const isLoaded = !!portfolioName;
    const isMobile = menu?.isMobile();

    const homeLink = isMobile ? undefined : 'home';

    const portfolioLabel = portfolioName || tLoading;
    const propertyLabel = propertyName || tLoading;

    let breadcrumbs: BreadcrumbItem[] = [];
    setBackLink(homeLink);

    if (path.startsWith('/portfolios')) {
      setBackLink(homeLink);
      breadcrumbs = [{ label: tProperty, link: '/portfolios' }];

      if (path.includes('/units/add')) {
        setBackLink(() => goBack);
        breadcrumbs.push(
          { label: portfolioLabel, link: isLoaded ? `/portfolios/${portfolioId}` : path },
          { label: propertyLabel, link: isLoaded ? `/portfolios/${portfolioId}/properties/${propertyId}` : path },
          { label: t('Add unit') || tLoading }
        );
      } else if (path.includes('/units/')) {
        setBackLink(() => goBack);
        breadcrumbs = [
          { label: portfolioLabel, link: isLoaded ? `/portfolios/${portfolioId}` : path },
          { label: propertyLabel, link: isLoaded ? `/portfolios/${portfolioId}/properties/${propertyId}` : path },
          { label: unitName || tLoading },
        ];
      } else if (path.includes('/properties/')) {
        setBackLink(() => goBack);
        if (isMobile) {
          breadcrumbs = [];
        }
        breadcrumbs.push(
          { label: portfolioLabel, link: isLoaded ? `/portfolios/${portfolioId}` : path },
          { label: propertyLabel, link: path }
        );
        if (path.endsWith('/edit')) {
          breadcrumbs = [
            { label: portfolioLabel, link: isLoaded ? `/portfolios/${portfolioId}` : path },
            { label: propertyLabel, link: isLoaded ? `/portfolios/${portfolioId}/properties/${propertyId}` : path },
            { label: t('Edit') },
          ];
        } else if (path.endsWith('/owners/add')) {
          breadcrumbs = [{ label: t('Add owner') }];
        } else if (/\/owners\/\d{5,}/.test(path)) {
          breadcrumbs = [{ label: t('Edit owner') }];
        }
      } else if (path.includes('/add-property')) {
        setBackLink(() => goBack);
        breadcrumbs = [{ label: t('Add property') || tLoading }];
      }
    } else if (path.startsWith('/listing')) {
      setBackLink(homeLink);
      breadcrumbs = [{ label: t('Listing'), link: `/listings/${portfolioId}` }];

      if (path.endsWith('/edit-listing')) {
        setBackLink(() => goBack);
        if (!isMobile) {
          breadcrumbs.push({
            label: unitListingDetail()
              ? `${unitListingDetail()?.property?.displayName} - ${unitListingDetail()?.unit?.name}`
              : t('Listing details'),
            link: `/listings/${portfolioId}/details/${unitListingDetail()?.id}`,
          });
        }
        breadcrumbs.push({ label: t('Edit listing') });
      } else if (path.includes('/details/')) {
        setBackLink(() => goBack);
        breadcrumbs.push({
          label: unitListingDetail()
            ? `${unitListingDetail()?.property?.displayName} - ${unitListingDetail()?.unit?.name}`
            : t('Listing details'),
        });
      }
    } else if (path.startsWith('/leasing')) {
      breadcrumbs = [{ label: t('Leasing'), link: path }];

      const unitInfo = [
        { label: portfolioLabel, link: isLoaded ? `/portfolios/${portfolioId}` : path },
        { label: propertyLabel, link: isLoaded ? `/portfolios/${portfolioId}/properties/${propertyId}` : path },
        { label: unitName || tLoading, link: isLoaded ? `/portfolios/${portfolioId}/properties/${propertyId}/units/${unitId}/ ` : path },
      ];

      if (path.includes('/edit-lease')) {
        setBackLink(() => goBack);
        breadcrumbs = [...unitInfo, { label: t('Edit lease') }];
      } else if (path.includes('/create-lease-draft')) {
        setBackLink(() => goBack);
        breadcrumbs = [...unitInfo, { label: t('Create Lease Document'), link: path }];
      } else if (path.includes('/renewal-of-lease')) {
        setBackLink(() => goBack);
        breadcrumbs = [...unitInfo, { label: t('Add renewal offer'), link: path }];
      } else if (/\/leases\/\d{5,}/.test(path)) {
        setBackLink(() => goBack);
        breadcrumbs = [...unitInfo];

        breadcrumbs.push({ label: t('Lease detail') });
      } else if (path.includes('/leases/new')) {
        setBackLink(homeLink);
        breadcrumbs.push({ label: t('Set up a new lease'), link: path });
      } else if (path.includes('/lease-templates')) {
        setBackLink(homeLink);
        breadcrumbs.push({ label: t('Lease template'), link: path });
        if (/\/lease-templates\/\d{5,}/.test(path)) {
          setBackLink(() => goBack);
          breadcrumbs.push({ label: presentableTitle()?.title || t('Loading') });
        }
      } else if (path.includes('/rental-applications')) {
        const rentalPath = '/leasing/rental-applications';
        setBackLink(homeLink);
        breadcrumbs.push({ label: t('Rental applications'), link: rentalPath });

        if (/\d{5,}/.test(path)) {
          const isDraft = path.includes('draft-rental-applications');
          setBackLink(() => goBack);
          breadcrumbs.push(
            { label: isDraft ? t('Unfinished') : t('Submitted'), link: `${rentalPath}${isDraft ? '/draft-rental-applications' : ''}` },
            { label: getTenantFullName(applicationStore.application) || emptyPlaceholder }
          );
        } else if (path.includes('/new')) {
          setBackLink(() => goBack);
          breadcrumbs.push({ label: t('New rental application'), link: '/leasing/rental-applications/new' });
        }
      } else if (path.includes('/add-offline-payment')) {
        setBackLink(homeLink);
        breadcrumbs.push({ label: t('Add offline payments'), link: '/leasing/add-offline-payment' });
      }
    } else if (path.startsWith('/users')) {
      breadcrumbs = [{ label: t('Users'), link: path }];
      if (path.includes('/property-managers')) {
        breadcrumbs.push({ label: t('Property managers'), link: '/users/property-managers' });
      } else if (path.includes('/tenants')) {
        breadcrumbs.push({ label: t('Tenants'), link: '/users/tenants' });

        if (path.includes('tenants/add')) {
          setBackLink(() => goBack);
          breadcrumbs.push({ label: t('Add a new tenant'), link: '/users/tenants/add' });
        } else if (/\d{5,}/.test(path)) {
          setBackLink(() => goBack);
          const isPaymentsMethodsPage = useMatch(() => `/users/tenants/:id/payments/methods`);
          const tenantId = path.match(/\d+/g)?.[0];
          breadcrumbs.push({ label: getTenantFullName(currentTenant()) || tLoading, link: `/users/tenants/${tenantId}` });

          if (isPaymentsMethodsPage()) {
            breadcrumbs.push({ label: t('Payment method') });
          } else if (path.includes('/edit')) {
            breadcrumbs.push({ label: t('Edit tenant') });
          }
        }
      } else if (path.includes('/owners')) {
        breadcrumbs.push({ label: t('Owners'), link: '/users/owners' });

        if (path.includes('owners/add')) {
          setBackLink(() => goBack);
          breadcrumbs.push({ label: t('Add a new owner'), link: '/users/owners/add' });
        } else if (/\d{5,}/.test(path)) {
          setBackLink(() => goBack);
          const isPaymentsMethodsPage = useMatch(() => `/users/owners/:id/payments/methods`);
          const ownerId = path.match(/\/users\/owners\/(\d+)\/edit/)?.[1];
          breadcrumbs.push({ label: getTenantFullName(currentOwner()) || tLoading, link: `/users/owners/${ownerId}` });

          if (isPaymentsMethodsPage()) {
            breadcrumbs.push({ label: t('Payment method') });
          } else if (path.includes('/edit')) {
            breadcrumbs.push({ label: t('Edit owner') });
          }
        } else if (path.includes('tax-documents')) {
          setBackLink(() => goBack);
          breadcrumbs.push({ label: t('Tax documents'), link: '/users/owners/tax-documents' });

          if (path.includes('generate')) {
            breadcrumbs.push({ label: t('Generate tax documents'), link: '/users/owners/generate' });
          }
        }
      }
    } else if (path.startsWith('/accounting')) {
      breadcrumbs = [{ label: t('Accounting'), link: path }];

      if (path.startsWith('/accounting/bank-accounts')) {
        breadcrumbs.push({ label: t('Bank accounts'), link: '/accounting/bank-accounts' });
        if (path.includes('/edit')) {
          setBackLink(() => goBack);
          breadcrumbs.push({ label: t('Edit bank account'), link: path });
        } else if (hasId(path)) {
          setBackLink(() => goBack);
          breadcrumbs.push({ label: t('Bank account details'), link: path });
        } else if (path.endsWith('/add')) {
          setBackLink(() => goBack);
          breadcrumbs.push({ label: t('Add a bank account'), link: path });
        }
      } else if (path.startsWith('/accounting/bills')) {
        breadcrumbs.push({ label: t('Bills'), link: '/accounting/bills' });
        if (path.includes('add')) {
          setBackLink(() => goBack);
          breadcrumbs.push({ label: t('Add a bill') });
        } else if (path.includes('/accounting/bills/pay')) {
          setBackLink(() => goBack);
          breadcrumbs.push({ label: t('Pay bills') });
        } else if (hasId(path)) {
          setBackLink(() => goBack);
          breadcrumbs.push({ label: t('Edit bill') });
        }
      } else if (path.startsWith('/accounting/chart-of-accounts')) {
        breadcrumbs.push({ label: t('Chart of Accounts'), link: '/accounting/chart-of-accounts' });
      } else if (path.startsWith('/accounting/company-revenue')) {
        breadcrumbs.push({ label: t('Company Revenue'), link: '/accounting/company-revenue' });
        if (path.includes('/management-fees')) {
          setBackLink(() => goBack);
          if (path.includes('/management-fees/add')) {
            breadcrumbs.push({ label: t('Calculate management fee') });
          } else if (hasId(path)) {
            breadcrumbs.push({ label: t('Management fee detail') });
          }
        } else if (path.includes('/pay-outs')) {
          setBackLink(() => goBack);
          if (path.includes('/add')) {
            breadcrumbs.push({ label: t('Add payout') });
          } else if (path.includes('/calculate')) {
            breadcrumbs.push({ label: t('Calculate pay outs') });
          }
        } else if (path.includes('/invoices') && hasId(path)) {
          setBackLink(() => goBack);
          breadcrumbs.push(
            { label: t('Invoices'), link: '/accounting/company-revenue/invoices' },
            { label: t('Invoice detail'), link: path }
          );
        }
      } else if (path.startsWith('/accounting/owner-funds')) {
        breadcrumbs.push({ label: t('Owner Funds'), link: '/accounting/owner-funds' });
        if (path.includes('/distributions')) {
          setBackLink(() => goBack);
          if (path.includes('/calculate')) {
            breadcrumbs.push({ label: t('Calculate a owner distribution') });
          } else if (hasId(path)) {
            breadcrumbs.push({ label: t('Distribution details') });
          }
        } else if (path.includes('contributions')) {
          setBackLink(() => goBack);
          if (path.includes('/add')) {
            breadcrumbs.push({ label: t('Add a contribution') });
          } else if (hasId(path)) {
            breadcrumbs.push({ label: t('Contribution details') });
          }
        }
      } else if (path.startsWith('/accounting/deposit-slips')) {
        breadcrumbs.push({ label: t('Deposit Slips'), link: '/accounting/deposit-slips' });
        if (hasId(path)) {
          setBackLink(() => goBack);
          breadcrumbs.push({ label: t('Deposit slip detail') });
        } else if (path.includes('/add')) {
          breadcrumbs.push({ label: t('Create new deposit slip') });
        }
      } else if (path.startsWith('/accounting/checks')) {
        breadcrumbs.push({ label: t('Checks'), link: '/accounting/checks' });
      }
    } else if (path.startsWith('/reports')) {
      breadcrumbs = [{ label: t('Reports'), link: '/reports' }];
      for (const key in reportPages) {
        if (path.includes(key)) {
          setBackLink(() => goBack);
          breadcrumbs.push({ ...reportPages[key], linkClass: 'text-black' });
          return breadcrumbs;
        }
      }
    } else if (path.startsWith('/maintenance')) {
      breadcrumbs = [{ label: t('Maintenance'), link: path }];
      if (path.includes('/maintenance/vendors')) {
        breadcrumbs.push({ label: t('Vendors'), link: '/maintenance/vendors' });

        if (path.includes('/add')) {
          setBackLink(() => goBack);
          breadcrumbs.push({ label: t('Add vendor'), link: '/maintenance/vendors/add' });
        } else if (hasId(path)) {
          setBackLink(() => goBack);
          breadcrumbs.push({ label: currentVendor()?.name || tLoading, link: `/maintenance/vendors/${currentVendor()?.id}` });

          if (path.includes('/edit')) {
            setBackLink(() => goBack);
            breadcrumbs.push({ label: t('Edit'), link: path });
          }
        } else if (path.includes('tax-documents')) {
          setBackLink(() => goBack);
          breadcrumbs.push({ label: t('Tax documents'), link: '/maintenance/vendors/tax-documents' });

          if (path.includes('generate')) {
            breadcrumbs.push({ label: t('Generate tax documents'), link: '/maintenance/vendors/tax-documents/generate' });
          }
        }
      } else if (path.includes('/maintenance/maintenance-request')) {
        breadcrumbs.push({ label: 'Maintenance request', link: '/maintenance/maintenance-request' });

        if (path.includes('/maintenance-request/add')) {
          setBackLink(() => goBack);
          breadcrumbs.push({ label: t('Add maintenance request'), link: '/maintenance/maintenance-request/add' });
        } else if (path.includes('/edit')) {
          setBackLink(() => goBack);
          breadcrumbs.push({ label: t('Edit maintenance request') });
        } else if (hasId(path)) {
          setBackLink(() => goBack);
          breadcrumbs.push({ label: t('Maintenance request details') });
        }
      } else if (path.includes('/work-orders')) {
        breadcrumbs.push({ label: 'Work orders', link: '/maintenance/work-orders' });

        if (path.includes('/work-orders/add')) {
          setBackLink(() => goBack);
          breadcrumbs.push({ label: t('Add work order'), link: '/maintenance/work-orders/add' });
        } else if (path.includes('/edit')) {
          setBackLink(() => goBack);
          breadcrumbs.push({ label: t('Edit work order'), link: path });
        } else if (hasId(path)) {
          setBackLink(() => goBack);
          breadcrumbs.push({ label: t('Work order details'), link: path });
        }
      } else if (path.includes('/ai-runbooks')) {
        breadcrumbs.push({ label: 'AI Runbooks', link: '/maintenance/ai-runbooks' });

        if (hasId(path)) {
          setBackLink(() => goBack);
          breadcrumbs.push({ label: t('AI runbook detail'), link: path });
        }
      }
    } else if (path.startsWith('/associations')) {
      breadcrumbs = [];
      if (!isMobile) {
        breadcrumbs = [
          { label: t('Associations'), link: path },
          { label: t('Violations'), link: '/associations/violations' },
        ];
      }

      if (path.includes('/edit')) {
        setBackLink(() => goBack);

        breadcrumbs.push({
          label: t('Edit violation'),
        });
      } else if (hasId(path)) {
        setBackLink(() => goBack);
        breadcrumbs.push({
          label: t('Violation detail'),
          link: path,
        });
      } else if (path.includes('/add')) {
        setBackLink(() => goBack);

        breadcrumbs.push({
          label: t('Add association violation'),
        });
      }
    } else if (path.startsWith('/communications')) {
      breadcrumbs = [{ label: t('Communications'), link: '/communications/dashboard' }];
      if (path.includes('/announcements')) {
        breadcrumbs.push({
          label: 'Announcements',
          link: '/communications/announcements',
        });

        if (path.includes('/add')) {
          setBackLink(() => goBack);
          breadcrumbs = [
            { label: '...', link: path },
            {
              label: t('New announcement'),
            },
          ];
        } else if (hasId(path)) {
          setBackLink(() => goBack);
          breadcrumbs = [
            { label: '...', link: path },
            {
              label: t('Announcement detail'),
              link: path,
            },
          ];
        }
      } else if (path.includes('/archive-messages')) {
        breadcrumbs.push({ label: t('Archive messages'), link: '/communications/archive-messages' });

        if (hasId(path)) {
          setBackLink(() => goBack);
          breadcrumbs = [
            { label: '...', link: path },
            {
              label: t('Archive message detail'),
            },
          ];
        }
      }
    } else if (path.startsWith('/settings')) {
      breadcrumbs = [{ label: t('Settings'), link: path }];
    }
    if (breadcrumbs.length > 0) {
      breadcrumbs.forEach((breadcrumb) => {
        if (!breadcrumb.link || breadcrumb.link === path) {
          breadcrumb.linkClass = 'text-text-level03 cursor-default';
        }
      });
      breadcrumbs[breadcrumbs.length - 1].linkClass = 'text-black cursor-default';
      return breadcrumbs.filter(Boolean);
    }

    const unknown: { label: string }[] = [{ label: t('Unknown Page') }];
    return unknown;
  });

  createEffect(() => {
    breadcrumbItems();
    updateVisibleBreadcrumbs();
  });

  function getTextWidth(text: string, font = '14px Poppins, Arial, sans-serif'): number {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    if (!context) {
      throw new Error('2D context not supported or canvas already in use');
    }

    context.font = font;
    const metrics = context.measureText(text);
    return metrics.width;
  }

  async function updateVisibleBreadcrumbs() {
    const items = breadcrumbItems();

    if (items.length < 2) {
      setVisibleBreadcrumbs(items);
      return;
    }

    const first = items[0];
    const last = items[items.length - 1];
    const middle = items.slice(1, -1);

    const windowWidth = window.innerWidth;
    const parentNode = containerRef?.parentElement?.parentElement;
    const lis = containerRef?.getElementsByClassName('real-li');

    const containerWidth = parentNode ? parentNode.offsetWidth : containerRef?.offsetWidth || 0;
    const path = location.pathname.toLowerCase();

    let availableWidth: number;
    if (windowWidth <= 640) {
      availableWidth = containerWidth - 300;
      await setVisibleBreadcrumbs([last]);

      if (!lis || !lis.length || !lis[lis.length - 1]) {
        return;
      }
      const maxWidth = getTextWidth(items[items.length - 1].label) + availableWidth;
      if (availableWidth < 0 || getTextWidth(items[items.length - 1].label) > availableWidth) {
        (lis[lis.length - 1] as HTMLLIElement).style.width = availableWidth + 'px';
      } else {
        (lis[lis.length - 1] as HTMLLIElement).style.width = 'auto';
      }
      return;
    } else if (windowWidth <= 1024) {
      availableWidth = containerWidth - 180;
    } else if (windowWidth <= 1280) {
      availableWidth = containerWidth - 430;
    } else {
      availableWidth = containerWidth - 540;
    }

    let sideElementWidth = 0;
    sideElementWidth = getTextWidth(items[0].label) + getTextWidth(items[items.length - 1].label) + 20 * 4 + 55;

    availableWidth = availableWidth - sideElementWidth;

    const visibleMiddle: BreadcrumbItem[] = [];
    let totalMiddleWidth = 0;

    for (const [index, item] of middle.entries()) {
      totalMiddleWidth += getTextWidth(item.label);

      if (totalMiddleWidth < availableWidth) {
        visibleMiddle.push(item);
      } else {
        const hiddenLabels = middle
          .slice(index)
          .map((i) => i.label)
          .join(' / ');
        visibleMiddle.push({
          label: '...',
          link: path,
          title: hiddenLabels,
          linkClass: 'text-text-level03 cursor-default',
        });
        break;
      }
    }

    await setVisibleBreadcrumbs([first, ...visibleMiddle, last]);

    if (!lis || !lis.length || !lis[lis.length - 1]) {
      return;
    }

    if (availableWidth < 0) {
      const maxWidth = getTextWidth(items[items.length - 1].label) + availableWidth;
      (lis[lis.length - 1] as HTMLLIElement).style.width = maxWidth + 'px';
    } else {
      (lis[lis.length - 1] as HTMLLIElement).style.width = 'auto';
    }
  }

  onMount(() => {
    updateVisibleBreadcrumbs();
    const observer = new ResizeObserver(() => {
      updateVisibleBreadcrumbs();
    });

    if (containerRef) {
      observer.observe(document.body);
    }
    onCleanup(() => observer.disconnect());
  });

  return (
    <div ref={(el) => (containerRef = el)} class="w-full">
      <Breadcrumb backLink={backLink()} items={visibleBreadcrumbs()} />
    </div>
  );
};

export default BreadcrumbContainer;
